namespace Solovis.Reports.Parameters.AttributionReport {

  export interface AttributionOptions {
    style?: string;                          // "arithmetic" | "geometric"
    interactionEffect?: string;              // "withSelection" | "withAllocation" | "standalone"
    includeLineItemBenchmarkEffect?: boolean;
    showContributions?: boolean;
    showVariances?: boolean;
    showStartingWeight?: boolean;
    showAvgWeight?: boolean;
    showEndingWeight?: boolean;
    showBenchmarkNames?: boolean;
  }

  // Help text. InteractionEffect, LineItemBenchmarkEffect, and ShowVariances are taken verbatim
  // from the AR-011-1 acceptance criteria; the remaining entries are descriptive drafts to be
  // confirmed with Product.
  const StyleHelp =
    "How returns are linked across sub-periods. Arithmetic sums single-period effects; Geometric " +
    "compounds them so the attribution effects reconcile to the geometrically linked active return.";

  const InteractionEffectHelp =
    "When using the 'With Selection Effect' or 'With Allocation Effect' method, the interaction " +
    "effect is combined with the selected effect. Choose 'Standalone' to display it as a separate value.";

  const LineItemBenchmarkEffectHelp =
    "Measures the contribution to active return attributable to the difference between each line " +
    "item's individual benchmark and the policy benchmark. Enable when line items have been " +
    "assigned their own benchmarks.";

  const ShowContributionsHelp =
    "Show each line item's contribution to the total return (weight x return) alongside its attribution effects.";

  const ShowVariancesHelp =
    "Show each line item's Weight Variance (overweight or underweight relative to the benchmark; " +
    "positive = overweight, negative = underweight) and Return Variance (portfolio return minus " +
    "benchmark return). When Show Contributions is also enabled, also shows Contribution Variance.";

  const ShowStartingWeightHelp =
    "Show each line item's portfolio weight at the start of the period.";

  const ShowAvgWeightHelp =
    "Show each line item's average portfolio weight over the period.";

  const ShowEndingWeightHelp =
    "Show each line item's portfolio weight at the end of the period.";

  const ShowBenchmarkNamesHelp =
    "Show the name of the benchmark applied to each line item.";

  // Defaults mirror the Edit Attribution mockup (AR-011-1).
  const getDefaultOptions = (): AttributionOptions => ({
    style: "arithmetic",
    interactionEffect: "withSelection",
    includeLineItemBenchmarkEffect: false,
    showContributions: false,
    showVariances: false,
    showStartingWeight: false,
    showAvgWeight: false,
    showEndingWeight: true,
    showBenchmarkNames: true
  });

  /**
   * "Attribution" parameters-bar control for the Attribution Report. Renders a Configure link
   * that opens the Edit Attribution modal (AR-011-1).
   */
  export class AttributionParameterControl extends Solovis.UI.BaseControl {
    private _options: AttributionOptions;

    constructor(options) {
      super(options);
      this._options = (typeof options.initialValue !== "undefined" && options.initialValue !== null)
        ? options.initialValue
        : getDefaultOptions();
    }

    public doRender() {
      const self = this;

      const configureLink = $("<a>").text("Configure").appendTo(this.container);
      configureLink.click(function () { self._showEditor(); });

      const editLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      editLink.click(function () { self._showEditor(); });
    }

    private _showEditor() {
      const editorDialog = new EditAttributionDialog({
        options: this._options,
        saveCallback: (data: AttributionOptions) => this.setParameterValue(data)
      });
      editorDialog.show();
    }

    public _setCurrentOptions(data) {
      this._options = data ?? getDefaultOptions();
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, AttributionParameterControl, this._setCurrentOptions, newValue, more);
    }

    public getParameterValue() { return Solovis.deepCopy(this._options); }

    public notifyParameterValueChanged() { $(this).trigger("parameterValueChanged"); }
  }


  export class EditAttributionDialog {
    private _opts: AttributionOptions;
    private _saveCallback: (data: AttributionOptions) => void;
    private _formBuilder: Solovis.UI.InputControls.FormBuilder;

    public constructor(options: { options: AttributionOptions; saveCallback: (data: AttributionOptions) => void; }) {
      this._opts = options.options ? Solovis.deepCopy(options.options) : getDefaultOptions();
      this._saveCallback = options.saveCallback;
    }

    public show() {
      const opts = this._opts;

      const formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 560,
        labelColumnWidth: 240
      });
      this._formBuilder = formBuilder;

      formBuilder.addEntry({
        name: "style",
        label: "Style",
        help: StyleHelp,
        initialValue: opts.style ?? "arithmetic",
        editor: {
          type: "select",
          selectOptions: {
            data: [
              { text: "Arithmetic", value: "arithmetic" },
              { text: "Geometric", value: "geometric" }
            ]
          }
        }
      });

      formBuilder.addEntry({
        name: "interactionEffect",
        label: "Interaction Effect",
        help: InteractionEffectHelp,
        initialValue: opts.interactionEffect ?? "withSelection",
        editor: {
          type: "select",
          selectOptions: {
            data: [
              { text: "with Selection Effect", value: "withSelection" },
              { text: "with Allocation Effect", value: "withAllocation" },
              { text: "Standalone", value: "standalone" }
            ]
          }
        }
      });

      const addBool = (name: keyof AttributionOptions, label: string, help?: string) => {
        formBuilder.addEntry({
          name: name as string,
          label: label,
          help: help,
          initialValue: !!opts[name],
          editor: { type: "bool" }
        });
      };

      addBool("includeLineItemBenchmarkEffect", "Include Line Item Benchmark Effect", LineItemBenchmarkEffectHelp);
      addBool("showContributions", "Show Contributions", ShowContributionsHelp);
      addBool("showVariances", "Show Variances", ShowVariancesHelp);
      addBool("showStartingWeight", "Show Starting Weight", ShowStartingWeightHelp);
      addBool("showAvgWeight", "Show Avg Weight", ShowAvgWeightHelp);
      addBool("showEndingWeight", "Show Ending Weight", ShowEndingWeightHelp);
      addBool("showBenchmarkNames", "Show Benchmark Names", ShowBenchmarkNamesHelp);

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Attribution",
        desiredWidth: 600,
        desiredHeight: 520,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          const values = this._formBuilder.getValues();
          const newOptions: AttributionOptions = {
            style: values.style,
            interactionEffect: values.interactionEffect,
            includeLineItemBenchmarkEffect: !!values.includeLineItemBenchmarkEffect,
            showContributions: !!values.showContributions,
            showVariances: !!values.showVariances,
            showStartingWeight: !!values.showStartingWeight,
            showAvgWeight: !!values.showAvgWeight,
            showEndingWeight: !!values.showEndingWeight,
            showBenchmarkNames: !!values.showBenchmarkNames
          };
          this._saveCallback(newOptions);
          return true;
        }
      });
    }
  }
}
